import { SignalsStoreService } from './../signals-store.service';
import { HttpErrorResponse, HttpEvent, HttpEventType, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { Observable, catchError, finalize, tap, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { ModalContentService } from '../modal-content/modal-content.service';
import { ModalContentTypes } from '../constants/modal-content-types';
import { DateTime } from 'luxon';
import { LocalStorageService } from '../local-storage.service';
import { LOCALSTORAGE_KEYS } from '../constants/databases';
import { Session } from '../types/session.type';
import { FirebaseCrudService } from '../firebase-crud.service';
import { environment } from '../../../environments/environment';
import { LoadingService } from '../loading.service';
import { preventErrorForLog, redactErrorLogPayload } from '../common/error-log.util';

// Error queue for deferred Firebase logging - prevents blocking critical path
interface QueuedError {
  req: HttpRequest<any>;
  error: HttpErrorResponse;
  localStorageService: LocalStorageService;
  firebaseCrudService: FirebaseCrudService;
}

let errorQueue: QueuedError[] = [];
let isProcessingQueue = false;

export function httpInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown> | any> {

  const modalService = inject(ModalContentService);
  const localStorageService = inject(LocalStorageService);
  const firebaseCrudService = inject(FirebaseCrudService);
  const loadingService = inject(LoadingService);
  const signalsStoreService = inject(SignalsStoreService);

  if (req.method !== 'GET')
    loadingService.validateEndPoint(req.url);

  return next(req).pipe(
    finalize(() => loadingService.isShowGlobalLoading() && loadingService.close()),
    catchError((error) => validateError(req, error, modalService, localStorageService, firebaseCrudService, signalsStoreService.requirePaymentMethodOpened())),
    tap(event => {
      if (event && event.type === HttpEventType.Response) {
        if (!environment.production)
          console.log(req.url, 'returned a response with status', event.status);
      }
    }));
}

function validateError(
  req: HttpRequest<unknown>,
  error: HttpErrorResponse,
  modalService: any,
  localStorageService: LocalStorageService,
  firebaseCrudService: FirebaseCrudService,
  requirePaymentMethodOpened: boolean
): Observable<HttpEvent<any>> {

  const preventErrorLog = preventErrorForLog(error.status, error.url);
  if (!preventErrorLog) {
    // Queue error for deferred processing instead of immediate Firebase write
    queueErrorForFirebase(req as HttpRequest<any>, error, localStorageService, firebaseCrudService);
  }

  switch (error.status) {
    case 444:
      if (requirePaymentMethodOpened)
        break;
      modalService.openModal(ModalContentTypes.PENDING_PAYMENT_METHOD_NO_SIGNUP, {
        title: 'Please Update Your Payment Method',
        closeable: false,
        closeOthers: true,
        focus: false
      });
      break;
    default:
      break;
  }

  return throwError(() => error);
}


/**
 * Queue error for deferred Firebase logging.
 * This prevents Firebase Auth from blocking the critical path during startup.
 */
function queueErrorForFirebase(
  req: HttpRequest<any>,
  error: HttpErrorResponse,
  localStorageService: LocalStorageService,
  firebaseCrudService: FirebaseCrudService
) {
  errorQueue.push({ req, error, localStorageService, firebaseCrudService });

  // Schedule queue processing during idle time
  if (!isProcessingQueue) {
    isProcessingQueue = true;
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => processErrorQueue(), { timeout: 10000 });
    } else {
      // Fallback for Safari
      setTimeout(() => processErrorQueue(), 5000);
    }
  }
}

/**
 * Process queued errors and send them to Firebase.
 * Runs during browser idle time to avoid blocking user interactions.
 */
function processErrorQueue() {
  const errors = [...errorQueue];
  errorQueue = [];
  isProcessingQueue = false;

  errors.forEach(({ req, error, localStorageService, firebaseCrudService }) => {
    sendErrorToFirebase(req, error, localStorageService, firebaseCrudService);
  });
}

function sendErrorToFirebase(
  httpRequest: HttpRequest<any>,
  error: HttpErrorResponse,
  localStorageService: LocalStorageService,
  firebaseCrudService: FirebaseCrudService
) {
  try {
    const currentUTCDateTime = DateTime.utc();
    const currentDateArray = currentUTCDateTime.toString().split('T');
    const currentStringDate = currentDateArray[0];
    const currentStringTime = currentDateArray[1].split('.')[0];



    let errorLog: any = {
      payload: redactErrorLogPayload(httpRequest.body),
      method: httpRequest.method,
      statusCode: error.status,
      statusText: error.statusText,
      url: error.url,
      timestamp: currentUTCDateTime.toMillis(),
      createdAt: `${currentStringDate} ${currentStringTime}`,
      createdAtDate: currentStringDate,
      createdAtTime: currentStringTime,
      message1: error?.error?.message || 'Unknow error',
      message2: error?.message || 'Unknow error',
      errorName: error?.name || null,
      errorType: error?.type || null,
    }

    // Session data:
    const storedSession: Session | null = localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    if (storedSession) {
      errorLog.clientId = storedSession.accountInfo.id;
    }

    firebaseCrudService.addErrorLog(errorLog);
  } catch (error) {
    console.log('Error trying to send data to Firebase: ', error);
  }
}
